import { z } from 'zod';
import { ITEM_TYPES } from '../money';
import { ORDER_STATUSES } from '../status';
import { PATTERN_MIN_POINTS } from '../pattern';
import { equipmentFieldsSchema } from './equipment';
import { BACKUP_ITEM_KINDS, BACKUP_STATUSES, SERVICE_TYPES } from '../orders';
import {
  booleanQuery,
  dateOnlySchema,
  idSchema,
  intSchema,
  moneySchema,
  optionalText,
  paginationQuerySchema,
  requiredText,
  timeSchema,
} from './common';

export const orderItemSchema = z
  .object({
    type: z.enum(ITEM_TYPES),
    productId: idSchema.nullish(),
    serviceId: idSchema.nullish(),
    description: requiredText(160, 'Descrição do item'),
    quantity: intSchema('Quantidade', 1, 9999),
    unitPrice: moneySchema,
    warrantyDays: intSchema('Garantia', 0, 3650),
    approved: z.boolean().default(true),
  })
  .superRefine((item, ctx) => {
    if (item.type === 'PRODUCT' && item.serviceId) {
      ctx.addIssue({ code: 'custom', path: ['serviceId'], message: 'Peça não pode apontar para um serviço' });
    }
    if (item.type === 'SERVICE' && item.productId) {
      ctx.addIssue({ code: 'custom', path: ['productId'], message: 'Serviço não pode apontar para um produto' });
    }
  });
export type OrderItemInput = z.infer<typeof orderItemSchema>;

export const unlockPatternSchema = z
  .array(z.number().int().min(1).max(9))
  .min(PATTERN_MIN_POINTS, `O padrão precisa ligar pelo menos ${PATTERN_MIN_POINTS} pontos`)
  .max(9)
  .refine((p) => new Set(p).size === p.length, 'Um ponto não pode se repetir no padrão');

/** Senha do aparelho: `undefined` = não alterar; `null`/"" = remover. */
const devicePasswordField = z
  .string()
  .max(100, 'Senha muito longa')
  .nullish()
  .transform((v) => (v === undefined ? undefined : v ? v : null));

export const backupItemSchema = z
  .object({
    kind: z.enum(BACKUP_ITEM_KINDS, { errorMap: () => ({ message: 'Item de backup inválido' }) }),
    label: optionalText(60, 'Descrição do item de backup'),
  })
  .superRefine((i, ctx) => {
    if (i.kind === 'OTHER' && !i.label) ctx.addIssue({ code: 'custom', path: ['label'], message: 'Descreva o item de backup' });
  })
  .transform((i) => ({ kind: i.kind, label: i.kind === 'OTHER' ? i.label ?? null : null }));

/** Lista ordenada por prioridade; itens fixos não se repetem. */
export const backupItemsSchema = z
  .array(backupItemSchema)
  .max(12, 'Máximo de 12 itens de backup')
  .superRefine((items, ctx) => {
    const seen = new Set<string>();
    items.forEach((i, idx) => {
      const key = i.kind === 'OTHER' ? `OTHER:${(i.label ?? '').toLowerCase()}` : i.kind;
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: [idx], message: 'Item de backup repetido' });
      seen.add(key);
    });
  });

/** Data de compra: não pode ser futura (1 dia de folga para fuso horário). */
const purchaseDateSchema = dateOnlySchema
  .refine((v) => Date.parse(`${v}T00:00:00Z`) <= Date.now() + 24 * 3600_000, 'Data de compra não pode ser futura')
  .nullish();

/** Campos de atendimento/garantia/backup. Regras entre campos ficam no backend (dependem do estado salvo). */
export const orderServiceFieldsSchema = z.object({
  serviceType: z.enum(SERVICE_TYPES, { errorMap: () => ({ message: 'Tipo de atendimento inválido' }) }),
  relatedOrderId: idSchema.nullish(),
  warrantyInvoiceNumber: optionalText(60, 'Nº da nota fiscal'),
  warrantyPurchaseDate: purchaseDateSchema,
  warrantyStore: optionalText(120, 'Loja'),
  warrantyManufacturer: optionalText(80, 'Fabricante'),
  backupRequired: z.boolean().nullish(),
  backupStatus: z.enum(BACKUP_STATUSES).nullish(),
  backupItems: backupItemsSchema,
  backupNotes: optionalText(1000, 'Observação do backup'),
});

const orderFieldsSchema = z.object({
  clientId: idSchema,
  equipmentId: idSchema.optional(),
  technicianId: idSchema.nullish(),
  reportedProblem: requiredText(4000, 'Defeito relatado'),
  technicalDiagnosis: optionalText(4000, 'Diagnóstico'),
  physicalCondition: optionalText(500, 'Estado físico'),
  accessoriesReceived: optionalText(300, 'Acessórios entregues'),
  notes: optionalText(4000, 'Observações'),
  entryDate: dateOnlySchema,
  entryTime: timeSchema.nullish(),
  devicePassword: devicePasswordField,
  unlockPattern: unlockPatternSchema.nullish(),
  items: z.array(orderItemSchema).max(200, 'Itens demais nesta OS').default([]),
  ...orderServiceFieldsSchema.shape,
  serviceType: orderServiceFieldsSchema.shape.serviceType.default('NORMAL'),
  backupItems: backupItemsSchema.default([]),
});

export const orderCreateSchema = orderFieldsSchema
  .extend({
    /** Cadastro do equipamento direto na criação da OS (fluxo "Adicionar equipamento"). */
    newEquipment: equipmentFieldsSchema.optional(),
    /** Registros de "serviço executado" digitados antes de salvar a OS pela primeira vez. */
    initialMaintenance: z.array(requiredText(2000, 'Serviço realizado')).max(50).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.equipmentId && !v.newEquipment) {
      ctx.addIssue({
        code: 'custom',
        path: ['equipmentId'],
        message: 'Selecione um equipamento ou cadastre um novo para continuar',
      });
    }
    if (v.equipmentId && v.newEquipment) {
      ctx.addIssue({ code: 'custom', path: ['newEquipment'], message: 'Informe apenas um equipamento' });
    }
    if ((v.serviceType === 'EM_GARANTIA' || v.serviceType === 'RETORNO') && !v.relatedOrderId) {
      ctx.addIssue({ code: 'custom', path: ['relatedOrderId'], message: 'Selecione a OS de origem' });
    }
  });
export type OrderCreateInput = z.infer<typeof orderCreateSchema>;

export const orderUpdateSchema = orderFieldsSchema.partial().extend({
  /** Quando enviado, substitui toda a lista de itens. */
  items: z.array(orderItemSchema).max(200).optional(),
  serviceType: orderServiceFieldsSchema.shape.serviceType.optional(),
  backupItems: backupItemsSchema.optional(),
});
export type OrderUpdateInput = z.infer<typeof orderUpdateSchema>;

export const statusChangeSchema = z.object({
  status: z.enum(ORDER_STATUSES, { errorMap: () => ({ message: 'Status inválido' }) }),
  note: optionalText(1000, 'Observação'),
  /** Data/hora real da entrega ou do encerramento (ISO). Vale para ENTREGUE e NAO_APROVADO; padrão: agora. */
  deliveredAt: z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), 'Data de entrega inválida')
    .optional(),
});
export type StatusChangeInput = z.infer<typeof statusChangeSchema>;

export const MAINTENANCE_VISIBILITIES = ['INTERNAL', 'CLIENT_VISIBLE'] as const;
export type MaintenanceVisibility = (typeof MAINTENANCE_VISIBILITIES)[number];
export const MAINTENANCE_VISIBILITY_LABELS: Record<MaintenanceVisibility, string> = {
  INTERNAL: 'Interno',
  CLIENT_VISIBLE: 'Visível ao cliente',
};

export const maintenanceCreateSchema = z.object({
  description: requiredText(2000, 'Serviço realizado', 2),
  /** Padrão seguro: interno. Só aparece para o cliente (PDF/portal) se marcado como visível. */
  visibility: z.enum(MAINTENANCE_VISIBILITIES, { errorMap: () => ({ message: 'Visibilidade inválida' }) }).default('INTERNAL'),
});
export type MaintenanceCreateInput = z.infer<typeof maintenanceCreateSchema>;

/** Na edição, visibilidade ausente significa "manter a atual" (sem o default de INTERNAL). */
export const maintenanceUpdateSchema = z.object({
  description: maintenanceCreateSchema.shape.description,
  visibility: z.enum(MAINTENANCE_VISIBILITIES, { errorMap: () => ({ message: 'Visibilidade inválida' }) }).optional(),
});
export type MaintenanceUpdateInput = z.infer<typeof maintenanceUpdateSchema>;

export const photoCaptionSchema = z.object({
  caption: optionalText(160, 'Legenda'),
});

export const orderListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(ORDER_STATUSES).optional(),
  hideCompleted: booleanQuery,
  clientId: idSchema.optional(),
  equipmentId: idSchema.optional(),
  sort: z.enum(['recent', 'oldest', 'number']).default('recent'),
});
export type OrderListQuery = z.infer<typeof orderListQuerySchema>;

/** Busca de OS candidatas a origem de garantia/retorno. */
export const originCandidatesQuerySchema = z.object({
  clientId: idSchema,
  equipmentId: idSchema.optional(),
  excludeOrderId: idSchema.optional(),
  search: z.string().trim().max(100).optional(),
});
export type OriginCandidatesQuery = z.infer<typeof originCandidatesQuerySchema>;
